import {
  Box,
  Link as MuiLink,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'

import logoWhiteSrc from '~/assets/images/corp/logo-white.svg'
import MarketingLink from '~/components/marketingLink'
import useIsPartner from '~/features/partners/hooks/useIsPartner'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import { ESiteImagesTypes } from '~/features/partners/models'
import { EMarketingClientPath } from '~/models/api'
import rem, { getWhiteOrBlackTextColorByBackground } from '~/utils/ui'

type TProps = {
  sx?: SxProps<Theme>
}

function Logo({ sx = {} }: TProps) {
  const theme = useTheme()
  const isPartner = useIsPartner()
  const { partner } = usePartnerCentralized()
  const getHolidayData = () => {
    const today = new Date()
    const month = today.getMonth() // 0 = Jan, 11 = Dec
    const date = today.getDate()

    // Christmas Tree: Dec 1 - Dec 25
    if (month === 11 && date < 26) {
      return { icon: '🎄', title: 'Happy Holidays from Replify!' }
    }

    // New Year: Dec 26 - Jan 7
    if ((month === 11 && date >= 26) || (month === 0 && date <= 7)) {
      return { icon: '🥂', title: 'Happy New Year from Replify!' }
    }

    // Valentine's Day: Feb 1 - Feb 14
    if (month === 1 && date >= 1 && date <= 14) {
      return { icon: '💕', title: "Happy Valentine's Day from Replify!" }
    }

    if (month === 2 && date >= 1 && date <= 31) {
      return { icon: '🌷', title: "It's Spring!" }
    }

    if (month === 4 && date >= 1 && date <= 14) {
      return { icon: '💐', title: "Happy Mother's Day from Replify!" }
    }

    if (month === 5 && date >= 18 && date <= 25) {
      return { icon: '👔', title: "Happy Father's Day from Replify!" }
    }

    return null
  }

  const holidayData = getHolidayData()

  return (
    <MuiLink
      component={MarketingLink}
      to={isPartner ? '#' : EMarketingClientPath.home}
      sx={{
        textDecoration: 'none',
        mr: 2,
        cursor: isPartner ? 'initial' : 'pointer',
        paddingTop: rem(8),
        paddingLeft: rem(23),
        ...sx,
      }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          gap: 2,
        }}
      >
        {isPartner ? (
          <Box
            sx={{
              alignSelf: 'center',
              flex: '1 0 auto',
              height: rem(32),
              width: { xxxs: rem(108), sm: rem(129) },
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
              backgroundImage: `url(${
                partner?.siteImages?.find(
                  (i) => i.type === ESiteImagesTypes.portalNavigationLogo,
                )?.imageUrl
              })`,
            }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignContent: 'center',
              justifyContent: 'center',
              gap: 0,
            }}
          >
            <Box
              sx={{
                height: rem(20),
                width: { xxxs: rem(108), sm: rem(129) },
                backgroundImage: `url(${logoWhiteSrc})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: rem(4) }}>
              <Typography
                variant="caption"
                sx={{
                  color: getWhiteOrBlackTextColorByBackground(
                    theme.palette.menuBackground,
                    theme,
                  ),
                  fontWeight: 'semibold',
                  letterSpacing: rem(2.1),
                  fontSize: rem(11),
                  lineHeight: rem(11),
                }}
              >
                PORTAL
              </Typography>
              {holidayData && (
                <Tooltip title={holidayData.title}>
                  <Box
                    sx={{
                      fontSize: rem(22),
                      padding: rem(2),
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: rem(28),
                      height: rem(28),
                      marginTop: rem(-10),
                    }}
                  >
                    {holidayData.icon}
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}
      </Stack>
    </MuiLink>
  )
}

export default Logo
